// Lightweight, consent-gated analytics for BookFreely.
//
// Rules
// - We only fire events when `getConsent().analytics === true`.
// - Payload is intentionally minimal: page path, referrer, viewport, LCP.
// - No personal identifiers. Not tied to the auth token.
// - Server drops the event if signal-to-noise is bad (unknown types).

import axios from "axios";
import { getConsent } from "./consent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "bf.analytics.sid";

function ensureSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2)) + "-" + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "anon";
  }
}

let flushTimer = null;
const queue = [];

function drop() { queue.length = 0; }

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(async () => {
    flushTimer = null;
    if (!queue.length) return;
    const consent = getConsent();
    if (!consent?.analytics) { drop(); return; }
    const batch = queue.splice(0, queue.length);
    try {
      await axios.post(`${API}/analytics/events`, { events: batch }, { timeout: 5000 });
    } catch {
      // best-effort; drop silently if server is down
    }
  }, 1500);
}

export function trackEvent(type, extra = {}) {
  const consent = getConsent();
  if (!consent?.analytics) return; // No consent = no event.
  queue.push({
    type,
    path: typeof window !== "undefined" ? window.location.pathname : "",
    ts: Date.now(),
    sid: ensureSessionId(),
    vw: typeof window !== "undefined" ? window.innerWidth : null,
    ...extra,
  });
  scheduleFlush();
}

export function trackPageView(path) {
  trackEvent("page_view", { path });
}

// One-shot: report the biggest content paint after each route (useful for
// spotting slow pages so we can actually fix them, per the user brief).
export function initPerfObserver() {
  if (typeof window === "undefined") return;
  try {
    const po = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === "largest-contentful-paint") {
          trackEvent("lcp", { ms: Math.round(entry.startTime) });
        }
      }
    });
    po.observe({ type: "largest-contentful-paint", buffered: true });
  } catch {
    // Older browsers — ignore
  }
}
